import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { BRAND } from '@repo/shared/brand';
import type { Gender, ReportReason, SearchPrefs } from '@repo/shared/domain';
import { countryFlag, type LatLon } from '@repo/shared/geo';
import { PLANS, type EntitlementResponse, type PlanId } from '@repo/shared/plans';
import type { ClientGeo } from '@repo/shared/protocol';
import type { SettingsUpdate } from '../App.tsx';
import { useChatSession } from '../hooks/useChatSession.ts';
import { useDebounced } from '../hooks/useDebounced.ts';
import { useElapsedSeconds } from '../hooks/useElapsedSeconds.ts';
import { useRemoteModeration } from '../hooks/useRemoteModeration.ts';
import { useSwipe } from '../hooks/useSwipe.ts';
import { useTransient } from '../hooks/useTransient.ts';
import {
  countryName,
  filterChipLabel,
  formatCount,
  formatDuration,
  QUALITY_LABELS,
} from '../labels.ts';
import { devActivatePlan, type Entitlement } from '../lib/entitlement.ts';
import {
  DEV_LOCATIONS,
  LocationError,
  loadDevLocation,
  requestBrowserLocation,
  saveDevLocation,
  type DevLocation,
} from '../lib/location.ts';
import { listInputs, mediaErrorText, openTrack, type DeviceOption } from '../lib/media.ts';
import type { ChatSession, SearchRequest } from '../lib/session.ts';
import type { Settings } from '../lib/settings.ts';
import { playConnectSound } from '../lib/sound.ts';
import { ChatPanel } from './ChatPanel.tsx';
import { Controls } from './Controls.tsx';
import { DevPanel } from './DevPanel.tsx';
import { DeviceSheet } from './DeviceSheet.tsx';
import { FilterSheet } from './FilterSheet.tsx';
import { Icon } from './Icon.tsx';
import { PlansDialog } from './PlansDialog.tsx';
import { ReportDialog } from './ReportDialog.tsx';
import { StatusOverlay } from './StatusOverlay.tsx';
import { VideoView } from './VideoView.tsx';

export interface ChatScreenProps {
  localStream: MediaStream;
  gender: Gender;
  settings: Settings;
  entitlement: Entitlement | null;
  onSettingsChange: SettingsUpdate;
  onEntitlement: (response: EntitlementResponse | null) => void;
  onExit: () => void;
}

type Panel = 'filters' | 'plans' | 'report' | 'chat' | 'devices' | null;

const LOCATION_UNAVAILABLE = 'Dein Standort ist gerade nicht verfügbar.';

export function ChatScreen(props: ChatScreenProps) {
  const session = useChatSession(props.localStream);
  return session ? <ChatRoom key={session.id} session={session} {...props} /> : null;
}

function ChatRoom({
  session,
  localStream,
  gender,
  settings,
  entitlement,
  onSettingsChange,
  onEntitlement,
  onExit,
}: ChatScreenProps & { session: ChatSession }) {
  const snapshot = useSyncExternalStore(session.subscribe, session.getSnapshot);
  const [panel, setPanel] = useState<Panel>(null);
  const [reportMatchId, setReportMatchId] = useState<string | null>(null);
  const [devLocation, setDevLocation] = useState<DevLocation | null>(loadDevLocation);
  const [browserLocation, setBrowserLocation] = useState<LatLon | null>(null);
  const [locating, setLocating] = useState(false);
  const [locationProblem, setLocationProblem] = useState<string | null>(null);
  const [locationAttempt, setLocationAttempt] = useState(0);
  const [seenChatId, setSeenChatId] = useState(0);
  const [inputs, setInputs] = useState<{ cameras: DeviceOption[]; mics: DeviceOption[] }>({
    cameras: [],
    mics: [],
  });
  const [deviceBusy, setDeviceBusy] = useState<'video' | 'audio' | null>(null);
  const [deviceProblem, setDeviceProblem] = useState<string | null>(null);
  /** Gespräch, für das der Ton schon gespielt wurde. */
  const soundedMatch = useRef<string | null>(null);
  /** true = die nächste Änderung der Suchanfrage startet die Suche (neu). */
  const restartOnChange = useRef(true);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);

  const { prefs } = settings;
  const geo = useMemo<ClientGeo | null>(
    () =>
      devLocation
        ? { lat: devLocation.lat, lon: devLocation.lon, country: devLocation.country }
        : browserLocation,
    [devLocation, browserLocation],
  );
  const request = useMemo<SearchRequest>(() => ({ gender, prefs, geo }), [gender, prefs, geo]);
  const debouncedRequest = useDebounced(request, 400);
  const needsLocation = prefs.scope === 'local' && geo === null;

  const moderation = useRemoteModeration(remoteVideoRef, snapshot.remoteStream);
  const swipe = useSwipe(
    () => session.next(),
    snapshot.phase === 'connecting' || snapshot.phase === 'connected',
  );
  const toast = useTransient(snapshot.notice);
  const talkingFor = useElapsedSeconds(
    snapshot.phase === 'connected' ? snapshot.connectedSince : null,
  );

  // Kurzer Ton, sobald jemand da ist – einmal pro Gespräch.
  useEffect(() => {
    const { phase, matchId } = snapshot;
    if (phase !== 'connected' || !matchId || soundedMatch.current === matchId) return;
    soundedMatch.current = matchId;
    if (settings.sound) playConnectSound();
  }, [snapshot, settings.sound]);

  // Geräteliste nur laden, solange das Blatt offen ist.
  useEffect(() => {
    if (panel !== 'devices') return;
    let cancelled = false;
    const load = () => {
      void listInputs().then((found) => {
        if (!cancelled) setInputs(found);
      });
    };
    load();
    const media = navigator.mediaDevices;
    media?.addEventListener('devicechange', load);
    return () => {
      cancelled = true;
      media?.removeEventListener('devicechange', load);
    };
  }, [panel]);

  // Freischaltung (Kauf, Erneuerung, Kündigung) an den Server weitergeben.
  const entitlementToken = entitlement?.token ?? null;
  useEffect(() => {
    session.setEntitlement(entitlementToken);
  }, [session, entitlementToken]);

  // Gespeicherter Umkreis-Filter: erst den Standort holen, dann suchen.
  useEffect(() => {
    if (!needsLocation) return;
    let cancelled = false;
    setLocationProblem(null);
    requestBrowserLocation()
      .then((location) => {
        if (!cancelled) setBrowserLocation(location);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        session.stop();
        restartOnChange.current = true;
        setLocationProblem(error instanceof LocationError ? error.message : LOCATION_UNAVAILABLE);
      });
    return () => {
      cancelled = true;
    };
  }, [session, needsLocation, locationAttempt]);

  // Erste Suche – und Neustart nach Stopp, Fehler oder Standortproblem.
  useEffect(() => {
    if (needsLocation || !restartOnChange.current) return;
    restartOnChange.current = false;
    session.start(request);
  }, [session, request, needsLocation]);

  // Filteränderungen während der Suche – entprellt, weil der Umkreis-Regler bei jedem Schritt feuert.
  useEffect(() => {
    if (debouncedRequest.prefs.scope === 'local' && debouncedRequest.geo === null) return;
    session.updateSearch(debouncedRequest);
  }, [session, debouncedRequest]);

  // Pfeil nach rechts = nächste Person
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (panel !== null || event.key !== 'ArrowRight' || event.repeat) return;
      if (event.target instanceof Element && event.target.closest('input, textarea, select')) {
        return;
      }
      event.preventDefault();
      session.next();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [session, panel]);

  const lastLineId = snapshot.chat.at(-1)?.id ?? 0;
  useEffect(() => {
    if (panel === 'chat') setSeenChatId(lastLineId);
  }, [panel, lastLineId]);
  const unreadChat =
    panel !== 'chat' && snapshot.chat.some((line) => line.from === 'peer' && line.id > seenChatId);

  const startNow = () => {
    if (needsLocation) {
      setLocationAttempt((attempt) => attempt + 1);
      return;
    }
    restartOnChange.current = false;
    session.start(request);
  };

  /** Filter aus dem Filter-Dialog. Für den Umkreis wird vorher der Standort abgefragt. */
  const changePrefs = async (next: SearchPrefs) => {
    if (next.scope === 'local' && geo === null) {
      setLocating(true);
      try {
        setBrowserLocation(await requestBrowserLocation());
        setLocationProblem(null);
      } catch (error) {
        setLocationProblem(error instanceof LocationError ? error.message : LOCATION_UNAVAILABLE);
        return; // Umkreis ohne Standort nicht übernehmen
      } finally {
        setLocating(false);
      }
    }
    onSettingsChange((current) => ({ ...current, prefs: next }));
  };

  /** Aktionen aus Hinweisen und Fehlermeldungen: Filter anpassen und neu suchen. */
  const recover = (patch: Partial<SearchPrefs>) => {
    restartOnChange.current = true;
    setLocationProblem(null);
    onSettingsChange((current) => ({ ...current, prefs: { ...current.prefs, ...patch } }));
  };

  const changeDevLocation = (id: string | null) => {
    saveDevLocation(id);
    if (session.getSnapshot().error) restartOnChange.current = true;
    setDevLocation(DEV_LOCATIONS.find((location) => location.id === id) ?? null);
  };

  /** Nach Aktionen im Tarif-Dialog: speichern und – falls die Suche am Tarif scheiterte – neu starten. */
  const applyEntitlement = (response: EntitlementResponse | null) => {
    onEntitlement(response);
    session.setEntitlement(response?.token ?? null);
    if (session.getSnapshot().error?.code === 'plan_required') session.start(request);
  };

  // Nur in der Entwicklung – im Live-Build fällt der Code samt Dev-Endpunkt weg.
  const activateDevPlan = import.meta.env.DEV
    ? async (plan: PlanId) => {
        applyEntitlement(await devActivatePlan(plan));
        setPanel(null);
      }
    : undefined;

  /** Welches Gerät läuft gerade? Der Browser verrät es über die Einstellungen der Spur. */
  const activeDeviceId = (kind: 'video' | 'audio'): string | null => {
    const [track] = kind === 'video' ? localStream.getVideoTracks() : localStream.getAudioTracks();
    const id = track?.getSettings().deviceId;
    return typeof id === 'string' && id !== '' ? id : null;
  };

  const pickDevice = async (kind: 'video' | 'audio', id: string) => {
    if (deviceBusy !== null || id === activeDeviceId(kind)) return;
    setDeviceBusy(kind);
    setDeviceProblem(null);
    try {
      session.setLocalTrack(await openTrack(kind, id));
      onSettingsChange((current) => ({
        ...current,
        devices:
          kind === 'video'
            ? { ...current.devices, cameraId: id }
            : { ...current.devices, micId: id },
      }));
    } catch (error) {
      setDeviceProblem(mediaErrorText(error));
    } finally {
      setDeviceBusy(null);
    }
  };

  /** Reihum durch die Kameras, am Handy also von vorne nach hinten und zurück. */
  const switchCamera = () => {
    const { cameras } = inputs;
    if (cameras.length < 2) return;
    const index = cameras.findIndex((camera) => camera.id === activeDeviceId('video'));
    const next = cameras[(index + 1) % cameras.length];
    if (next) void pickDevice('video', next.id);
  };

  const openReport = () => {
    if (!snapshot.matchId) return;
    setReportMatchId(snapshot.matchId);
    setPanel('report');
  };

  const submitReport = (reason: ReportReason) => {
    if (reportMatchId) session.report(reason, reportMatchId);
    setPanel(null);
  };

  const sendChat = (text: string) => session.sendChat(text);
  const country = snapshot.detectedCountry ?? devLocation?.country ?? null;

  return (
    <div className="chat">
      <header className="topbar">
        <button
          type="button"
          className="wordmark wordmark--button"
          onClick={onExit}
          title="Zur Startseite"
        >
          <span className="logo" aria-hidden="true" />
          <span className="wordmark__text">{BRAND.name}</span>
        </button>
        <span className="online">
          <span className="online__dot" aria-hidden="true" />
          {formatCount(snapshot.online)} online
        </span>
        <div className="topbar__actions">
          <button type="button" className="chip" onClick={() => setPanel('filters')}>
            <Icon name="sliders" size={16} />
            <span className="chip__text">{filterChipLabel(prefs)}</span>
          </button>
          <button
            type="button"
            className={`chip${snapshot.plan === 'free' ? ' chip--upgrade' : ''}`}
            onClick={() => setPanel('plans')}
          >
            <Icon name="crown" size={16} />
            {snapshot.plan === 'free' ? 'Premium' : PLANS[snapshot.plan].name}
          </button>
        </div>
      </header>

      <main className="stage">
        {/* Gegenüber und du: zwei gleich große Bilder */}
        <div className="tiles">
          <div className="tiles__grid">
            <section
              className={`remote tile${snapshot.phase === 'connected' ? ' tile--live' : ''}${swipe.dragging ? ' remote--dragging' : ''}`}
              {...swipe.handlers}
            >
              <div className="remote__layer" style={swipe.style}>
                <VideoView
                  videoRef={remoteVideoRef}
                  stream={snapshot.remoteStream}
                  adaptiveFit
                  className={`remote__video${moderation.blurred ? ' remote__video--blurred' : ''}`}
                />
              </div>

              {snapshot.phase === 'connected' && snapshot.peer?.country && (
                <span className="badge">
                  {countryFlag(snapshot.peer.country)} {countryName(snapshot.peer.country)}
                </span>
              )}

              {snapshot.phase === 'connected' && (
                <div className="tile__meta">
                  <span className="meta">{formatDuration(talkingFor)}</span>
                  {snapshot.quality && (
                    <span
                      className="meta"
                      role="img"
                      aria-label={QUALITY_LABELS[snapshot.quality.level]}
                      title={
                        !snapshot.quality.rtt
                          ? QUALITY_LABELS[snapshot.quality.level]
                          : `${QUALITY_LABELS[snapshot.quality.level]}, ${snapshot.quality.rtt} ms`
                      }
                    >
                      <span
                        className={`quality quality--${snapshot.quality.level}`}
                        aria-hidden="true"
                      >
                        <span />
                        <span />
                        <span />
                      </span>
                    </span>
                  )}
                </div>
              )}

              {moderation.blurred ? (
                <div className="overlay">
                  <div className="card" role="alert">
                    <Icon name="eyeOff" size={28} />
                    <h2>Video ausgeblendet</h2>
                    <p>Das Bild könnte unangemessen sein.</p>
                    <div className="card__actions">
                      <button type="button" className="button" onClick={moderation.reveal}>
                        Trotzdem zeigen
                      </button>
                      <button type="button" className="button" onClick={openReport}>
                        Melden
                      </button>
                      <button
                        type="button"
                        className="button button--primary"
                        onClick={() => session.next()}
                      >
                        Weiter
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <StatusOverlay
                  snapshot={snapshot}
                  prefs={prefs}
                  needsLocation={needsLocation}
                  locationProblem={locationProblem}
                  onStart={startNow}
                  onRecover={recover}
                  onRetryLocation={() => setLocationAttempt((attempt) => attempt + 1)}
                  onOpenPlans={() => setPanel('plans')}
                  onExit={onExit}
                />
              )}
            </section>

            <div className="tile">
              <VideoView
                stream={localStream}
                muted
                mirrored
                adaptiveFit
                revision={snapshot.localRevision}
                className="tile__video"
              />
              {!snapshot.camEnabled && (
                <div className="tile__off">
                  <Icon name="camOff" />
                </div>
              )}
              <span className="tile__label">Du</span>
              <button
                type="button"
                className="tile__action"
                onClick={() => setPanel('devices')}
                aria-label="Kamera und Ton"
                title="Kamera und Ton"
              >
                <Icon name="switchCam" size={18} />
              </button>
            </div>
          </div>

          {snapshot.phase === 'connected' && (
            <p className="swipe-hint" aria-hidden="true">
              Nach links wischen für die nächste Person
            </p>
          )}
          {toast && (
            <div key={toast.id} className="toast" role="status">
              {toast.text}
            </div>
          )}
        </div>

        <aside className="side">
          <ChatPanel lines={snapshot.chat} enabled={snapshot.chatOpen} onSend={sendChat} />
        </aside>

        {panel === 'chat' && (
          <div className="chat-sheet" data-no-swipe>
            <ChatPanel
              lines={snapshot.chat}
              enabled={snapshot.chatOpen}
              onSend={sendChat}
              onClose={() => setPanel(null)}
            />
          </div>
        )}
      </main>

      <Controls
        phase={snapshot.phase}
        micEnabled={snapshot.micEnabled}
        camEnabled={snapshot.camEnabled}
        canReport={snapshot.matchId !== null}
        unreadChat={unreadChat}
        onToggleMic={() => session.setMicEnabled(!snapshot.micEnabled)}
        onToggleCam={() => session.setCamEnabled(!snapshot.camEnabled)}
        onStartStop={() => (snapshot.phase === 'idle' ? startNow() : session.stop())}
        onReport={openReport}
        onToggleChat={() => setPanel(panel === 'chat' ? null : 'chat')}
        onNext={() => (snapshot.phase === 'idle' ? startNow() : session.next())}
      />

      {panel === 'filters' && (
        <FilterSheet
          prefs={prefs}
          plan={snapshot.plan}
          countryLabel={country ? countryName(country) : null}
          locating={locating}
          locationProblem={locationProblem}
          onChange={(next) => void changePrefs(next)}
          onLocked={() => setPanel('plans')}
          onClose={() => setPanel(null)}
        />
      )}
      {panel === 'plans' && (
        <PlansDialog
          plan={snapshot.plan}
          planExpiresAt={snapshot.planExpiresAt}
          billing={snapshot.billing}
          devPlans={snapshot.devPlans}
          entitlement={entitlement}
          onEntitlement={applyEntitlement}
          onActivateDev={activateDevPlan}
          onClose={() => setPanel(null)}
        />
      )}
      {panel === 'report' && (
        <ReportDialog onSubmit={submitReport} onClose={() => setPanel(null)} />
      )}
      {panel === 'devices' && (
        <DeviceSheet
          cameras={inputs.cameras}
          mics={inputs.mics}
          cameraId={activeDeviceId('video')}
          micId={activeDeviceId('audio')}
          sound={settings.sound}
          busy={deviceBusy}
          problem={deviceProblem}
          onPick={(kind, id) => void pickDevice(kind, id)}
          onSwitchCamera={switchCamera}
          onToggleSound={(on) => onSettingsChange((current) => ({ ...current, sound: on }))}
          onClose={() => setPanel(null)}
        />
      )}
      {import.meta.env.DEV && (
        <DevPanel
          snapshot={snapshot}
          devLocation={devLocation}
          onDevLocationChange={changeDevLocation}
        />
      )}
    </div>
  );
}
